import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { LlmUsageAttribution, LlmUsageTraceSpan } from '../api/client'
import { StatusBadge, cronDisplayName } from './llmUsageAttribution'

export interface SpanTreeProps {
  spans: LlmUsageTraceSpan[]
  /** The trace root's own identity, shown on the root row. */
  attribution?: LlmUsageAttribution | null
  onLoadMore?: () => void
}

interface SpanNode {
  span: LlmUsageTraceSpan
  children: SpanNode[]
}

interface PlaceholderNode {
  state: 'omitted' | 'missing' | 'evicted'
  children: SpanNode[]
}

type TreeNode = SpanNode | PlaceholderNode

function isPlaceholder(node: TreeNode): node is PlaceholderNode {
  return 'state' in node
}

function startedAt(span: LlmUsageTraceSpan): number {
  const value = typeof span.started_at === 'string' ? Date.parse(span.started_at) : NaN
  return Number.isNaN(value) ? Number.MAX_SAFE_INTEGER : value
}

function displayValue(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return null
}

function spanName(span: LlmUsageTraceSpan): string {
  return displayValue(span.name) ?? displayValue(span.target) ?? span.span_id
}

function attribute(span: LlmUsageTraceSpan, name: string): unknown {
  const attrs = span.attributes
  if (attrs && typeof attrs === 'object') {
    return (attrs as Record<string, unknown>)[name]
  }
  return undefined
}

function hasAttribute(span: LlmUsageTraceSpan, name: string): boolean {
  return attribute(span, name) === true || span[name] === true
}

function numberValue(span: LlmUsageTraceSpan, name: string): number | null {
  const value = span[name]
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function spanChildren(spans: LlmUsageTraceSpan[]): TreeNode[] {
  const byId = new Map<string, SpanNode>()
  for (const span of spans) byId.set(span.span_id, { span, children: [] })

  const roots: TreeNode[] = []
  const placeholders = new Map<string, PlaceholderNode>()
  for (const node of byId.values()) {
    const parentId = node.span.parent_span_id
    if (!parentId) {
      roots.push(node)
      continue
    }

    const parent = byId.get(parentId)
    if (parent) {
      parent.children.push(node)
      continue
    }

    const state = node.span.parent_state === 'omitted' || node.span.parent_state === 'evicted'
      ? node.span.parent_state
      : 'missing'
    // A present parent can still be absent from a truncated response. Keep the
    // span visible under a labelled missing placeholder rather than dropping it.
    const placeholder = placeholders.get(`${state}:${parentId}`) ?? { state, children: [] }
    placeholder.children.push(node)
    placeholders.set(`${state}:${parentId}`, placeholder)
  }

  roots.push(...placeholders.values())
  const sort = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => {
      const aStarted = isPlaceholder(a) ? startedAt(a.children[0]?.span) : startedAt(a.span)
      const bStarted = isPlaceholder(b) ? startedAt(b.children[0]?.span) : startedAt(b.span)
      return aStarted - bStarted
    })
    for (const node of nodes) {
      if (!isPlaceholder(node)) sort(node.children)
      else node.children.sort((a, b) => startedAt(a.span) - startedAt(b.span))
    }
  }
  sort(roots)
  return roots
}

function SpanRow({
  span,
  depth,
  hasChildren,
  expanded,
  attribution,
  onToggle,
}: {
  span: LlmUsageTraceSpan
  depth: number
  hasChildren: boolean
  expanded: boolean
  attribution?: LlmUsageAttribution | null
  onToggle: () => void
}): ReactNode {
  const model = displayValue(span.model) ?? displayValue(span.response_model)
  const input = numberValue(span, 'input_tokens')
  const output = numberValue(span, 'output_tokens')
  const total = numberValue(span, 'total_tokens')
  const latency = numberValue(span, 'latency_ms')
  const causedBy = displayValue(span.caused_by_span_id)
  const unknownParent = hasAttribute(span, 'parent_tool_unknown')
  const startMissing = hasAttribute(span, 'start_missing')
  const incomplete = span.status !== 'incomplete' && hasAttribute(span, 'incomplete')
  // A cron named after the one activity it runs reports the same string twice;
  // printing it twice says nothing the first one did not.
  const identity = span.parent_span_id === null && attribution
    ? [attribution.activity, cronDisplayName(attribution)]
      .filter((value, index, values) => Boolean(value) && values.indexOf(value) === index)
      .join(' · ')
    : ''

  return (
    <div
      role="treeitem"
      aria-level={depth + 1}
      aria-expanded={hasChildren ? expanded : undefined}
      className="rounded border border-gray-200 bg-white px-3 py-2 shadow-sm"
      style={{ marginLeft: `${Math.min(depth, 8) * 1.25}rem` }}
    >
      <div className="flex min-w-0 items-center gap-2 text-sm">
        {hasChildren ? (
          <button
            type="button"
            onClick={onToggle}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${spanName(span)}`}
            className="w-4 shrink-0 text-gray-400"
          >
            {expanded ? '▾' : '▸'}
          </button>
        ) : <span className="w-4 shrink-0" aria-hidden="true" />}
        <span className="shrink-0 rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-indigo-700">
          {span.kind}
        </span>
        <span className="min-w-0 flex-1 truncate font-medium text-gray-800" title={spanName(span)}>
          {spanName(span)}
        </span>
        {/* The span's own outcome, distinct from the trace's capture state. */}
        <StatusBadge status={typeof span.status === 'string' ? span.status : null} />
        {model && <span className="shrink-0 truncate font-mono text-xs text-gray-500">{model}</span>}
        {total != null && <span className="shrink-0 text-xs text-gray-500">{total} tokens</span>}
        {latency != null && <span className="shrink-0 font-mono text-xs text-gray-500">{latency}ms</span>}
      </div>

      <div className="ml-6 mt-1 flex flex-wrap items-center gap-1.5 text-[10px] text-gray-500">
        {input != null && <span>input {input}</span>}
        {output != null && <span>output {output}</span>}
        {unknownParent && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">parent_tool_unknown</span>}
        {startMissing && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">start_missing</span>}
        {incomplete && <span className="rounded bg-orange-100 px-1.5 py-0.5 text-orange-800">incomplete</span>}
        {identity && <span className="font-mono text-gray-600">{identity}</span>}
        {causedBy && <span className="text-gray-600">requested by {causedBy}</span>}
      </div>

      <div className="ml-6 mt-1 h-1 overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
        <div className="h-full rounded-full bg-indigo-400" style={{ width: latency == null ? '0%' : `${Math.min(100, Math.max(4, latency))}%` }} />
      </div>
    </div>
  )
}

function PlaceholderRow({ state, depth }: { state: PlaceholderNode['state']; depth: number }): ReactNode {
  return (
    <div
      role="treeitem"
      aria-level={depth + 1}
      className="rounded border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-xs text-gray-500"
      style={{ marginLeft: `${Math.min(depth, 8) * 1.25}rem` }}
    >
      Parent {state}
    </div>
  )
}

function TreeBranch({
  node,
  depth,
  openState,
  attribution,
  onToggle,
}: {
  node: TreeNode
  depth: number
  openState: Map<string, boolean>
  attribution?: LlmUsageAttribution | null
  onToggle: (spanId: string, open: boolean) => void
}): ReactNode {
  if (isPlaceholder(node)) {
    return (
      <div className="space-y-2">
        <PlaceholderRow state={node.state} depth={depth} />
        {node.children.map(child => <TreeBranch key={child.span.span_id} node={child} depth={depth + 1} openState={openState} attribution={attribution} onToggle={onToggle} />)}
      </div>
    )
  }

  // The first two levels start open until the operator says otherwise.
  const isExpanded = openState.get(node.span.span_id) ?? depth < 2
  return (
    <div className="space-y-2">
      <SpanRow
        span={node.span}
        depth={depth}
        hasChildren={node.children.length > 0}
        expanded={isExpanded}
        attribution={attribution}
        onToggle={() => onToggle(node.span.span_id, !isExpanded)}
      />
      {isExpanded && node.children.map(child => (
        <TreeBranch key={isPlaceholder(child) ? child.state : child.span.span_id} node={child} depth={depth + 1} openState={openState} attribution={attribution} onToggle={onToggle} />
      ))}
    </div>
  )
}

export function SpanTree({ spans, attribution = null, onLoadMore }: SpanTreeProps) {
  const [openState, setOpenState] = useState<Map<string, boolean>>(() => new Map())
  const roots = useMemo(() => spanChildren(spans), [spans])

  const toggle = (spanId: string, open: boolean) => {
    setOpenState(previous => new Map(previous).set(spanId, open))
  }

  return (
    <div data-testid="span-tree" role="tree" aria-label="Span tree" className="space-y-2">
      {roots.map(node => (
        <TreeBranch
          key={isPlaceholder(node) ? `${node.state}-${node.children[0]?.span.parent_span_id ?? 'unknown'}` : node.span.span_id}
          node={node}
          depth={0}
          openState={openState}
          attribution={attribution}
          onToggle={toggle}
        />
      ))}
      {onLoadMore && (
        <button
          type="button"
          onClick={onLoadMore}
          className="rounded border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100"
        >
          Load more
        </button>
      )}
    </div>
  )
}
